import { Capacitor } from '@capacitor/core';
import { resolveCookieDomain } from 'shared/utils/dubReferral';

/**
 * Loads the Dub.co analytics script for referral click tracking.
 *
 * Injected as a plain script tag rather than via the @dub/analytics React
 * component: that package only exposes `@dub/analytics/react` through the
 * package.json "exports" map, which webpack 4 (react-scripts 4) and Jest 26 do
 * not resolve. The data-* attributes below mirror what @dub/analytics@0.0.32's
 * own inject() writes for domainsConfig and cookieOptions.
 *
 * Gated on REACT_APP_DUB_ENABLED === 'true' and skipped inside the Capacitor
 * native apps. Click capture does not depend on this script: see
 * shared/utils/dubReferral.js.
 */

const DUB_SCRIPT_SRC = 'https://www.dubcdn.com/analytics/script.js';
const DUB_COOKIE_EXPIRES_IN_DAYS = 90;

const isDubEnabled = () => process.env.REACT_APP_DUB_ENABLED === 'true';

const buildDubScriptAttributes = ({ referDomain, cookieDomain, hostname } = {}) => {
    const attributes = {};

    const refer = (referDomain || '').trim();
    if (refer) {
        attributes['data-domains'] = JSON.stringify({ refer });
    }

    const cookieOptions = { expiresInDays: DUB_COOKIE_EXPIRES_IN_DAYS, path: '/' };
    const domain = resolveCookieDomain(hostname, cookieDomain);
    if (domain) cookieOptions.domain = domain;
    attributes['data-cookie-options'] = JSON.stringify(cookieOptions);

    return attributes;
};

const installDubAnalytics = () => {
    try {
        if (!isDubEnabled()) return;
        if (Capacitor.isNativePlatform()) return;
        if (document.head.querySelector(`script[src="${DUB_SCRIPT_SRC}"]`)) return;

        const script = document.createElement('script');
        script.src = DUB_SCRIPT_SRC;
        script.defer = true;
        const attributes = buildDubScriptAttributes({
            referDomain: process.env.REACT_APP_DUB_REFER_DOMAIN,
            cookieDomain: process.env.REACT_APP_DUB_COOKIE_DOMAIN,
            hostname: window.location?.hostname,
        });
        Object.entries(attributes).forEach(([name, value]) => script.setAttribute(name, value));
        document.head.appendChild(script);
    } catch (e) {
        console.error('[Dub] failed to install analytics script', e);
    }
};

export { DUB_SCRIPT_SRC, buildDubScriptAttributes, installDubAnalytics };
